import {type RefObject, useCallback, useMemo, useRef, useState} from 'react'
import {useSubmitSuggestedChangesMutation} from '@github-ui/pull-requests/use-submit-suggested-changes-mutation'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

import {useDialogStateContext} from '../../contexts/DialogStateContext'

import type {SuggestedChange} from '../../conversation-types'
import {ApplyOrAddToBatch} from './CodeSuggestionActions/ApplyOrAddToBatch'
import {ApplySuggestionDialog} from './CodeSuggestionActions/ApplySuggestionDialog'
import {CodeSuggestionUnavailable} from './CodeSuggestionActions/CodeSuggestionUnavailable'
import {validateSuggestedChangeCanBeAddedToBatch} from '../../helpers/suggested-changes'
import {useComparisonInfo, useSuggestionBatch} from '@github-ui/pull-requests/pull-request-store-selectors'

export function CodeSuggestionActions({suggestedChange}: {suggestedChange: SuggestedChange}) {
  const {setIsDialogOpen} = useDialogStateContext()
  const [isApplySuggestionsDialogOpen, setIsApplySuggestionsDialogOpen] = useState(false)
  const returnFocusRef = useRef<RefObject<HTMLElement | null> | undefined>(undefined)
  const batchSuggestedChangesEnabled = useFeatureFlag('batch_suggested_changes')
  const suggestionBatch = useSuggestionBatch()

  const isPendingInBatch =
    batchSuggestedChangesEnabled &&
    suggestedChange.commentDatabaseId !== undefined &&
    suggestedChange.commentDatabaseId !== null &&
    suggestionBatch.activeBatch.some(
      id =>
        id.commentDatabaseId === suggestedChange.commentDatabaseId &&
        id.suggestionIndex === suggestedChange.suggestionIndex,
    )

  // Validate using the batch which now stores threadId and pathLineRanges
  const {isValid, reason} = validateSuggestedChangeCanBeAddedToBatch(
    {
      threadId: suggestedChange.threadId,
      pathLineRanges: [{path: suggestedChange.path, lineRange: suggestedChange.lineRange}],
    },
    suggestionBatch.activeBatch,
  )

  const changesToApply = useMemo(() => [suggestedChange], [suggestedChange])

  const authorLogins = changesToApply.map((change: SuggestedChange) => change.authorLogin)

  const setDialogOpen = useCallback(
    (isDialogOpen: boolean) => {
      setIsApplySuggestionsDialogOpen(isDialogOpen)
      setIsDialogOpen?.(isDialogOpen)
    },
    [setIsDialogOpen],
  )

  const closeDialog = useCallback(() => {
    setDialogOpen(false)

    // manually return focus to the button that opened the dialog
    if (returnFocusRef.current) {
      const ref = returnFocusRef.current.current
      setTimeout(() => ref?.focus())
    }
  }, [setDialogOpen])

  const openDialog = useCallback(
    (returnFocusTo?: RefObject<HTMLElement | null>) => {
      setDialogOpen(true)

      // store a ref to an element we will return focus to when the dialog closes
      returnFocusRef.current = returnFocusTo
    },
    [setDialogOpen, returnFocusRef],
  )

  const {mutate: submitSuggestions} = useSubmitSuggestedChangesMutation()
  const {
    fullDiff: {headOid},
  } = useComparisonInfo()
  const handleCommit = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      submitSuggestions(
        {changes: changesToApply, message: commitMessage, currentOid: headOid},
        {
          onSuccess: () => {
            closeDialog()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'submitSuggestedChanges', 'Failed to submit suggested changes')
          },
        },
      )
    },
    [changesToApply, closeDialog, headOid, submitSuggestions],
  )

  return (
    <div className="border-top p-2">
      <ApplyOrAddToBatch suggestedChange={suggestedChange} onOpenDialog={openDialog} canAddToBatch={isValid} />

      {!isPendingInBatch && !isValid && reason && <CodeSuggestionUnavailable reason={reason} className="m-0" />}

      {isApplySuggestionsDialogOpen && (
        <ApplySuggestionDialog
          authorLogins={authorLogins}
          batchSize={changesToApply.length}
          onClose={closeDialog}
          onCommit={handleCommit}
        />
      )}
    </div>
  )
}
